<script setup>
import { createLogger } from '@/@vxapp/@core/Logger/VxLog'
import { setPageTitle } from '@core/utils/pageMetaUtils.js'
import MessagesDrawer from '@/components/Drawers/MessagesDrawer.vue'

setPageTitle(PageMeta.Invoices.Preview.Title)
definePage({ meta: PageMeta.Invoices.Preview.Meta })

import InvoicesServicePage from '@/views/Previews/Invoices/InvoicesServicePage.vue'
import { useInvoiceStore } from '@/@vxapp/Invoices/useInvoiceStore'
import RefundButton from '@/views/Previews/PayPal/Buttons/RefundButton.vue'
import { useFileActions } from '@/composables/useFileActions'
import { NativeFileExtension } from '@/native/nativeFiles'
import { useAppNotifications } from '@/plugins/toast/composables/useAppNotifications'
import { getFileActionSuccessMessage } from '@/utils/fileActionFeedback'

const log = createLogger('[guid]')
const invoiceStore = useInvoiceStore()
const notify = useAppNotifications()
const { downloadFile } = useFileActions()

const route = useRoute()

const openMessagesDrawer = () => {
  invoiceStore.isMessagesDrawerOpen = true
}

const handleDownloadQuoteBtnPress = async () => {
  try {
    const result = await downloadFile({
      fallbackExtension: NativeFileExtension.Pdf,
      fileName: `Invoice ${invoiceStore.invoice.id}.pdf`,
      fileUrl: invoiceStore.messagesVariables.SERVICE_PDF_URL,
    })

    notify.success(getFileActionSuccessMessage(result, 'Invoice'))
  } catch {
    notify.error('Invoice could not be saved')
  }
}

const computedRefundDisabled = computed(() => {
  if (!invoiceStore.messagesVariables) {
    return true
  }

  if (!invoiceStore.messagesVariables.PAYMENT_REFUNDABLE) {
    return true
  }

  return false
})

const computedRefundData = computed(() => {
  if (!invoiceStore.messagesVariables) {
    return null
  }

  return {
    invoiceGUID: invoiceStore.invoice.invoiceGUID,
    vortexInvoiceId: invoiceStore.invoice.id,
    vortexOrderId: invoiceStore.messagesVariables.PAYPAL_VORTEX_ORDER_ID,
    provider: invoiceStore.messagesVariables.PAYMENT_REFUND_PROVIDER,
    grossAmount: invoiceStore.messagesVariables.SERVICE_BILLING_GROSS,
  }
})

// Reload the invoice and its message variables so the status and the refund button follow the
// refund the API recorded.
const refreshInvoice = async () => {
  const { responseCode } = await invoiceStore.fetch(route.params.guid)

  if (responseCode !== 200) {
    notify.error('The invoice could not be refreshed. Reload the page to see its current status.')
    log.error('Error refreshing the invoice after a refund')

    return false
  }

  return true
}

// The API records the refund on the invoice, so a refund or an already-refunded answer only
// needs the page to reload what the API now holds.
const handleRefundSuccess = refreshInvoice

const handleAlreadyRefunded = refreshInvoice

/* ****** */
</script>

<template>
  <section>
    <VRow>
      <VCol
        cols="12"
        md="9"
      >
        <!-- InvoicePreview -->
        <InvoicesServicePage />
      </VCol>
      <VCol
        cols="12"
        md="3"
      >
        <VCard class="mb-2">
          <!-- Submit Button -->
          <VCardText>
            <VBtn
              block
              prepend-icon="tabler-world-bolt"
              color="success"
              :to="{ name: `service-i-guid`, params: { guid: route.params.guid } }"
            >
              Service Page
            </VBtn>
          </VCardText>

          <VCardText>
            <VBtn
              block
              :prepend-icon="PageMeta.Invoices.Edit.Icon"
              color="primary"
              :to="{ name: PageMeta.Invoices.Edit.Name, params: { guid: route.params.guid } }"
            >
              Edit Invoice
            </VBtn>
          </VCardText>

          <VCardText>
            <VBtn
              block
              prepend-icon="tabler-download"
              variant="tonal"
              color="success"
              @click="handleDownloadQuoteBtnPress"
            >
              Download
            </VBtn>
          </VCardText>

          <VCardText>
            <RefundButton
              :hidden="computedRefundDisabled"
              :disabled="computedRefundDisabled"
              :text="PageMeta.Invoices.Refund.Title"
              :icon="PageMeta.Invoices.Refund.Icon"
              :refund-data="computedRefundData"
              @refund-success="handleRefundSuccess"
              @already-refunded="handleAlreadyRefunded"
            />
          </VCardText>

          <!-- -->
        </VCard>

        <!-- Messages Drawer -->

        <VCard class="mb-2">
          <VCardItem>
            <VBtn
              block
              prepend-icon="tabler-send"
              color="primary"
              @click="openMessagesDrawer"
            >
              Messages Drawer
            </VBtn>
          </VCardItem>
        </VCard>
        <MessagesDrawer
          v-if="invoiceStore.invoice"
          v-model:is-drawer-open="invoiceStore.isMessagesDrawerOpen"
          :service-g-u-i-d="invoiceStore.invoice.invoiceGUID"
          service-type="Invoices"
        />

        <!-- //Messages Drawer -->
      </VCol>
    </VRow>
  </section>
</template>

<style lang="scss">
@media print {
  .v-application {
    background: none !important;
  }

  @page {
    margin: 0;
    size: auto;
  }

  .layout-page-content,
  .v-row,
  .v-col-md-9 {
    padding: 0;
    margin: 0;
  }

  .product-buy-now {
    display: none;
  }

  .v-navigation-drawer,
  .layout-vertical-nav,
  .app-customizer-toggler,
  .layout-footer,
  .layout-navbar,
  .layout-navbar-and-nav-container {
    display: none;
  }

  .v-card {
    box-shadow: none !important;

    .print-row {
      flex-direction: row !important;
    }
  }

  .layout-content-wrapper {
    padding-inline-start: 0 !important;
  }
}
</style>
